export const BILLABLE_ACTIONS = {
  TRAINING_JOB: "training_job",
  EVAL_RUN_SAMPLE: "eval_run_sample",
  DEPLOYMENT_ACTIVE: "deployment_active",
  DEPLOYMENT_GPU_HOUR: "deployment_gpu_hour",
  SERVERLESS_INFERENCE: "serverless_inference",
  THIRD_PARTY_SERVERLESS_INFERENCE: "third_party_serverless_inference",
  PROXIED_INFERENCE_REQUESTS: "proxied_inference_requests",
  PROXIED_INFERENCE_DATA_BYTES: "proxied_inference_data_bytes",
  OTEL_SPAN_COUNT: "otel_span_count",
  OTEL_SPAN_DATA_BYTES: "otel_span_data_bytes",
  HALO_RUN: "halo_run",
  HALO_INFERENCE_COST: "halo_inference_cost",
  SIGNAL_SPAN_CLASSIFICATION: "signal_span_classification",
  SIGNAL_TRACE_CLASSIFICATION: "signal_trace_classification",
  SIGNAL_SESSION_CLASSIFICATION: "signal_session_classification",
} as const;

export const SUBSCRIPTION_TIERS = {
  FREE: "free",
  GROWTH: "growth",
  ENTERPRISE: "enterprise",
} as const;

export const LIMIT_FIELDS = {
  PER_MINUTE: "perMinute",
  PER_HOUR: "perHour",
  PER_DAY: "perDay",
  PER_MONTH: "perMonth",
  MAX_CONCURRENT: "maxConcurrent",
} as const;

export const LIMIT_OVERRIDE_SOURCES = {
  OPERATOR: "operator",
  PAY_AS_YOU_GO_CREDIT_PURCHASE: "pay_as_you_go_credit_purchase",
} as const;

/**
 * Operator override value meaning "unbounded". `team_limit_overrides.value` is
 * NOT NULL, so this sentinel is how an operator lifts a limit entirely (an
 * unbounded `serverless_inference.perMinute` resolves to RPM 0, which the edge
 * rate limiter treats as bypass).
 */
export const UNLIMITED_LIMIT_OVERRIDE_VALUE = -1;

export const SERVERLESS_INFERENCE_PAY_AS_YOU_GO_RPM_FLOOR = 250;
/**
 * RPM a team is raised to when an operator grants it free credits and it is
 * still on its tier default. Fast serverless models make the free-tier RPM
 * the first wall a credited user hits. Kept below the pay-as-you-go floor so
 * a real purchase still buys strictly more headroom than a free grant.
 */
export const SERVERLESS_INFERENCE_FREE_CREDIT_GRANT_RPM = 200;

export const NANOCREDITS_PER_CREDIT = 1_000_000_000n;
export const USD_CENTS_PER_CREDIT = 100n;
/** PostgreSQL `integer` maximum for invoice-payment attempt amounts in cents. */
export const MAX_BILLING_INVOICE_PAYMENT_ATTEMPT_CENTS = 2_147_483_647;
export const MAX_BILLING_INVOICE_PAYMENT_ATTEMPT_NANOCREDITS =
  (BigInt(MAX_BILLING_INVOICE_PAYMENT_ATTEMPT_CENTS) * NANOCREDITS_PER_CREDIT) /
  USD_CENTS_PER_CREDIT;
/** Token-count denominator for per-million-token rates. */
export const TOKENS_PER_MILLION = 1_000_000n;

export const NANOCREDITS_PER_USD_CENT =
  NANOCREDITS_PER_CREDIT / USD_CENTS_PER_CREDIT; // 10_000_000n

/**
 * Nanocredits per (token × cent-per-million-tokens). With
 * 1 credit = 1e9 nanocredits = 100 US cents, this is exactly
 * 10_000_000 / 1_000_000 = 10n, so pricing a per-million-cent rate against a
 * token count is exact integer math with no rounding at any token count.
 */
export const NANOCREDITS_PER_TOKEN_CENT_PER_MILLION =
  NANOCREDITS_PER_USD_CENT / TOKENS_PER_MILLION;

/**
 * A rate quoted in US cents per million tokens, expressed as USD per token —
 * the unit the observability row's `*_cost_per_token` columns carry.
 *
 * Lossy only in Float64 representation: the division itself is exact in real
 * arithmetic, and it scales the same integers
 * {@link NANOCREDITS_PER_TOKEN_CENT_PER_MILLION} does, so a row priced this
 * way multiplies back out to the nanocredit cost the charge recorded.
 */
export function usdPerTokenFromCentsPerMillion(
  centsPerMillion: number,
): number {
  return (
    centsPerMillion / Number(USD_CENTS_PER_CREDIT) / Number(TOKENS_PER_MILLION)
  );
}
export const MILLISECONDS_PER_HOUR = 3_600_000n;
/**
 * Lifetime count of free HALO runs a free-tier team gets before runs become
 * billable. Only successful runs consume this grant — see
 * ``countFreeIncludedHaloRunsByTeam``, which excludes failed runs so a team
 * is never charged a free run for a run that didn't deliver a result.
 */
export const HALO_FREE_TIER_INCLUDED_RUNS_PER_TEAM = 2;

export type BillableAction =
  (typeof BILLABLE_ACTIONS)[keyof typeof BILLABLE_ACTIONS];

/** The only actions funded by expiring Stripe subscription allowance. */
export const SUBSCRIPTION_ALLOWANCE_ACTIONS = new Set<BillableAction>([
  BILLABLE_ACTIONS.SERVERLESS_INFERENCE,
  BILLABLE_ACTIONS.THIRD_PARTY_SERVERLESS_INFERENCE,
]);

/** Signal classification scope → the billable action that scope settles to. */
export const SIGNAL_SCOPE_BILLABLE_ACTIONS = {
  span: BILLABLE_ACTIONS.SIGNAL_SPAN_CLASSIFICATION,
  trace: BILLABLE_ACTIONS.SIGNAL_TRACE_CLASSIFICATION,
  session: BILLABLE_ACTIONS.SIGNAL_SESSION_CLASSIFICATION,
} as const;

export type SignalClassificationScope =
  keyof typeof SIGNAL_SCOPE_BILLABLE_ACTIONS;

export type AggregateBillableAction =
  | typeof BILLABLE_ACTIONS.PROXIED_INFERENCE_REQUESTS
  | typeof BILLABLE_ACTIONS.PROXIED_INFERENCE_DATA_BYTES
  | typeof BILLABLE_ACTIONS.OTEL_SPAN_COUNT
  | typeof BILLABLE_ACTIONS.OTEL_SPAN_DATA_BYTES
  | typeof BILLABLE_ACTIONS.SIGNAL_SPAN_CLASSIFICATION
  | typeof BILLABLE_ACTIONS.SIGNAL_TRACE_CLASSIFICATION
  | typeof BILLABLE_ACTIONS.SIGNAL_SESSION_CLASSIFICATION;

export type SubscriptionTier =
  (typeof SUBSCRIPTION_TIERS)[keyof typeof SUBSCRIPTION_TIERS];

export type LimitField = (typeof LIMIT_FIELDS)[keyof typeof LIMIT_FIELDS];

export type LimitOverrideSource =
  (typeof LIMIT_OVERRIDE_SOURCES)[keyof typeof LIMIT_OVERRIDE_SOURCES];

/**
 * Convert a human-readable USD amount ("0.002", "0.50", "10") into exact
 * integer nanocredits (1 credit = $1 = 1e9 nanocredits). String-based so
 * sub-cent prices stay exact — no float rounding. Supports up to 9 decimal
 * places (one nanocredit).
 */
export function usdToNanocredits(usd: string): bigint {
  const match = /^(\d+)(?:\.(\d{1,9}))?$/.exec(usd);
  const whole = match?.[1];
  if (!match || whole === undefined) {
    throw new Error(`Invalid USD amount: ${usd}`);
  }
  const fraction = match[2] ?? "";
  return (
    BigInt(whole) * NANOCREDITS_PER_CREDIT + BigInt(fraction.padEnd(9, "0"))
  );
}

/**
 * How one billable action's charge cost is computed. Every action declares
 * its pricing model here so there is exactly one place to read "who decides
 * the price" — no zero-priced entries that silently rely on some other
 * module supplying the cost.
 *
 * - `perUnit`: settlement multiplies the charge quantity by the unit price.
 * - `flatFee`: every charge costs the same fixed amount.
 * - `callerSupplied`: the owning domain computes the cost and MUST pass
 *   `costNanocredits` to `createCharge`; billing throws if it is missing
 *   rather than silently charging zero.
 * - `metadataDerived`: billing-service derives the cost from the charge's
 *   typed metadata (e.g. GPU-hours x rate). The optional
 *   `estimatedNanocreditsPerUnit` feeds customer-facing overage estimates
 *   only — never settlement; omit it when no single per-unit figure is
 *   meaningful.
 * - `unpriced`: the action is entitlement-only and never costs anything.
 */
export type ActionPricing =
  | { model: "perUnit"; nanocreditsPerUnit: bigint }
  | { model: "flatFee"; nanocredits: bigint }
  | { model: "callerSupplied" }
  | { model: "metadataDerived"; estimatedNanocreditsPerUnit?: bigint }
  | { model: "unpriced" };

export const ACTION_PRICING = {
  [BILLABLE_ACTIONS.TRAINING_JOB]: {
    model: "metadataDerived",
    // $10 per job — overage-display estimate only; settlement derives the
    // real cost from GPU type and runtime.
    estimatedNanocreditsPerUnit: usdToNanocredits("10"),
  },
  // Actual inference cost (target + judge calls) plus markup, computed in
  // the eval workers from token usage and summed over settled samples.
  // Quota stays sample-count based (getMetadataQuantity reads
  // settledQuantity), so zero-cost all-BYOK runs must still record their
  // sample count.
  [BILLABLE_ACTIONS.EVAL_RUN_SAMPLE]: { model: "callerSupplied" },
  [BILLABLE_ACTIONS.DEPLOYMENT_ACTIVE]: { model: "unpriced" },
  // Cost depends on GPU tier × runtime; no single per-unit estimate is
  // meaningful (and per-hour deployment billing is still preview-gated).
  [BILLABLE_ACTIONS.DEPLOYMENT_GPU_HOUR]: { model: "metadataDerived" },
  [BILLABLE_ACTIONS.SERVERLESS_INFERENCE]: { model: "callerSupplied" },
  [BILLABLE_ACTIONS.THIRD_PARTY_SERVERLESS_INFERENCE]: {
    model: "callerSupplied",
  },
  [BILLABLE_ACTIONS.PROXIED_INFERENCE_REQUESTS]: {
    model: "perUnit",
    // Free: gateway inference volume is not billed. Kept perUnit so aggregate
    // billing still records the request quantity and usage counters.
    nanocreditsPerUnit: usdToNanocredits("0"),
  },
  [BILLABLE_ACTIONS.PROXIED_INFERENCE_DATA_BYTES]: {
    model: "perUnit",
    // Free: gateway inference storage is not billed. Kept perUnit so aggregate
    // billing still records the byte quantity and usage counters.
    nanocreditsPerUnit: usdToNanocredits("0"),
  },
  [BILLABLE_ACTIONS.OTEL_SPAN_COUNT]: {
    model: "perUnit",
    // Free: trace span volume is not billed. Kept perUnit so aggregate billing
    // still records the span quantity and usage counters.
    nanocreditsPerUnit: usdToNanocredits("0"),
  },
  [BILLABLE_ACTIONS.OTEL_SPAN_DATA_BYTES]: {
    model: "perUnit",
    // Free: trace span storage is not billed. Kept perUnit so aggregate billing
    // still records the byte quantity and usage counters.
    nanocreditsPerUnit: usdToNanocredits("0"),
  },
  [BILLABLE_ACTIONS.HALO_RUN]: {
    model: "flatFee",
    // $0.50 per settled run.
    nanocredits: usdToNanocredits("0.50"),
  },
  [BILLABLE_ACTIONS.HALO_INFERENCE_COST]: { model: "callerSupplied" },
  // Signal classifications: flat price per completed SLM classification on the
  // deepseek-v4-flash judge route ($0.14/1M input + $0.28/1M output). Priced
  // off the TYPICAL token cost at each scope, which scales with how much
  // content the classifier ingests:
  //   - span: one span's input+output (~1-2k tokens typical), bounded by
  //     MAX_SPAN_PROMPT_TOKENS so a pathologically large span can't cost more
  //     than this flat price.
  //   - trace / session: many spans assembled newest-first under the shared
  //     MAX_PROMPT_TOKENS budget. A trace bundles several spans and a session
  //     bundles several traces, so each step up ingests more on average —
  //     hence the widening price ladder. A budget-filling trace/session is
  //     rare and can exceed its flat price; accepted, because typical units
  //     sit well under the cap. Lower MAX_PROMPT_TOKENS to harden that margin.
  [BILLABLE_ACTIONS.SIGNAL_SPAN_CLASSIFICATION]: {
    model: "perUnit",
    // $0.001 per span classification.
    nanocreditsPerUnit: usdToNanocredits("0.001"),
  },
  [BILLABLE_ACTIONS.SIGNAL_TRACE_CLASSIFICATION]: {
    model: "perUnit",
    // $0.004 per trace classification.
    nanocreditsPerUnit: usdToNanocredits("0.004"),
  },
  [BILLABLE_ACTIONS.SIGNAL_SESSION_CLASSIFICATION]: {
    model: "perUnit",
    // $0.010 per session classification.
    nanocreditsPerUnit: usdToNanocredits("0.010"),
  },
} as const satisfies Readonly<Record<BillableAction, ActionPricing>>;

/**
 * Per-unit price for customer-facing overage estimates (plan cards, the
 * billing page's "Extra" column). Settled prices come from `ACTION_PRICING`
 * via billing-service; this helper only powers display math.
 *
 * Returns null when no meaningful per-unit estimate exists (caller-supplied
 * and unpriced actions, or metadataDerived without an estimate) so display
 * code shows nothing instead of a misleading $0.
 */
export function getOverageEstimateNanocreditsPerUnit(
  action: BillableAction,
): bigint | null {
  const pricing = ACTION_PRICING[action];
  switch (pricing.model) {
    case "perUnit":
      return pricing.nanocreditsPerUnit;
    case "metadataDerived":
      return "estimatedNanocreditsPerUnit" in pricing
        ? pricing.estimatedNanocreditsPerUnit
        : null;
    case "flatFee":
      return pricing.nanocredits;
    case "callerSupplied":
    case "unpriced":
      return null;
  }
}

/**
 * Format nanocredits as a customer-readable USD amount (1 credit = $1).
 * For error messages and logs — UI formatting lives in the web app.
 */
export function formatNanocreditsAsUsd(nanocredits: bigint): string {
  const negative = nanocredits < 0n;
  const absolute = negative ? -nanocredits : nanocredits;
  const cents = absolute / 10_000_000n;
  const dollars = cents / 100n;
  const remainder = (cents % 100n).toString().padStart(2, "0");
  return `${negative ? "-" : ""}$${dollars.toString()}.${remainder}`;
}

/**
 * One included (free) quota window an action settles against before credits.
 *
 * A charge draws from EVERY configured window at once: each window has a
 * remaining free amount (its quota minus what the team already used in that
 * window), and the SMALLEST remaining amount decides how much of the charge
 * is free. So an action with both a day and a month window gives usage away
 * only while it is inside both budgets. An action with no windows has no
 * included quota — every charged unit costs credits.
 *
 * The quota amount always comes from the action's effective limits row in
 * TIER_LIMITS (tier or custom plan, after overrides) — every per-tier
 * number lives in that one table:
 * - `effective_day_limit`: the perDay LIMIT doubles as the free daily
 *   amount — the classic quota-then-overage shape used by OTEL/proxy/
 *   training. An unbounded (null) limit grants no free quota.
 * - `included_per_month`: the explicit `includedPerMonth` field — a
 *   giveaway amount decoupled from the action's hard caps, used by signal
 *   classifications.
 */
export type IncludedQuotaWindow =
  | { window: "day"; source: "effective_day_limit" }
  | { window: "month"; source: "included_per_month" };

export const ACTION_INCLUDED_QUOTA_WINDOWS: Readonly<
  Record<BillableAction, readonly IncludedQuotaWindow[]>
> = {
  [BILLABLE_ACTIONS.TRAINING_JOB]: [
    { window: "day", source: "effective_day_limit" },
  ],
  // Eval samples have no free allowance: every incurred token bills at
  // provider cost + markup. The only unbilled eval is one whose analysis run
  // is marked ``free_included``, which the platform absorbs at the charge
  // seam rather than through a quota window.
  [BILLABLE_ACTIONS.EVAL_RUN_SAMPLE]: [],
  [BILLABLE_ACTIONS.DEPLOYMENT_ACTIVE]: [],
  [BILLABLE_ACTIONS.DEPLOYMENT_GPU_HOUR]: [],
  [BILLABLE_ACTIONS.SERVERLESS_INFERENCE]: [],
  [BILLABLE_ACTIONS.THIRD_PARTY_SERVERLESS_INFERENCE]: [],
  [BILLABLE_ACTIONS.PROXIED_INFERENCE_REQUESTS]: [
    { window: "day", source: "effective_day_limit" },
  ],
  [BILLABLE_ACTIONS.PROXIED_INFERENCE_DATA_BYTES]: [
    { window: "day", source: "effective_day_limit" },
  ],
  [BILLABLE_ACTIONS.OTEL_SPAN_COUNT]: [
    { window: "day", source: "effective_day_limit" },
  ],
  [BILLABLE_ACTIONS.OTEL_SPAN_DATA_BYTES]: [
    { window: "day", source: "effective_day_limit" },
  ],
  // HALO's free allowance is the lifetime included-run grant owned by the
  // halo domain (billingMode), not a billing-side quota window.
  [BILLABLE_ACTIONS.HALO_RUN]: [],
  [BILLABLE_ACTIONS.HALO_INFERENCE_COST]: [],
  [BILLABLE_ACTIONS.SIGNAL_SPAN_CLASSIFICATION]: [
    { window: "month", source: "included_per_month" },
  ],
  [BILLABLE_ACTIONS.SIGNAL_TRACE_CLASSIFICATION]: [
    { window: "month", source: "included_per_month" },
  ],
  [BILLABLE_ACTIONS.SIGNAL_SESSION_CLASSIFICATION]: [
    { window: "month", source: "included_per_month" },
  ],
};

const DAY_MS = 24 * 60 * 60 * 1_000;

/**
 * The idempotency horizon `H_accept`: the charge recorder refuses a charge
 * whose `occurredAt` is older than this. Ledger compaction drops the detail
 * rows the duplicate probe reads only once they are older than this plus a
 * partition, so every copy of a charge it accepts still finds its original.
 * One value for every process: the recorder, the replay tools and the
 * compactor must agree on it.
 */
export const CHARGE_ACCEPTANCE_HORIZON_MS = 60 * DAY_MS;

/**
 * Replay tools refuse a payload a day before the recorder would, so an old
 * replay fails in the tool instead of dead-lettering in a consumer.
 */
export const CHARGE_REPLAY_HORIZON_MS = CHARGE_ACCEPTANCE_HORIZON_MS - DAY_MS;

/** True when a charge for an event at `occurredAt` is too old to replay. */
export function isChargeBeyondReplayHorizon(
  occurredAt: Date,
  now: Date,
): boolean {
  return now.getTime() - occurredAt.getTime() > CHARGE_REPLAY_HORIZON_MS;
}
